import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Typography,
  Divider,
  Chip,
  Alert,
  Slider,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircleOutlineRounded,
  CancelOutlined,
  PersonRounded,
  WorkRounded,
  FamilyRestroomRounded,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { errorText } from '../../../lib/error-text';
import {
  scholarshipApi,
  type ScholarshipApplicationSnapshotDto,
  type ApplyScholarshipDiscountResultDto,
} from '../../../lib/api/scholarship.api';
import { formatNumber } from '../../../lib/i18n-format';

interface Props {
  open: boolean;
  onClose: () => void;
  application: ScholarshipApplicationSnapshotDto | null;
  tenantId: string;
  scholarshipCoveragePercent: number;
  scholarshipId: string;
}

const STATUS_LABELS: Record<
  string,
  { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }
> = {
  draft: { label: 'Borrador', color: 'default' },
  submitted: { label: 'Enviada', color: 'info' },
  reviewing: { label: 'En revisión', color: 'warning' },
  approved: { label: 'Aprobada', color: 'success' },
  rejected: { label: 'Rechazada', color: 'error' },
};

const EMPLOYMENT_LABELS: Record<string, string> = {
  employed: 'Empleado',
  unemployed: 'Desempleado',
  'part-time': 'Tiempo parcial',
};

export function ReviewApplicationDialog({
  open,
  onClose,
  application,
  tenantId,
  scholarshipCoveragePercent,
  scholarshipId,
}: Props) {
  const { t } = useTranslation('sis');
  const queryClient = useQueryClient();
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [coveragePercent, setCoveragePercent] = useState(scholarshipCoveragePercent);
  const [notes, setNotes] = useState('');
  // When an approval lands, keep the dialog open to show how the discount hit the plan.
  const [outcome, setOutcome] = useState<ApplyScholarshipDiscountResultDto | null>(null);

  const { mutate, isPending, error, reset } = useMutation({
    mutationFn: () => {
      if (!application || !decision) throw new Error('Missing data');
      const reviewParams: Parameters<typeof scholarshipApi.reviewApplication>[0] = {
        tenantId,
        applicationId: application.id,
        decision,
      };
      if (decision === 'approve') {
        reviewParams.approvedCoveragePercent = coveragePercent;
      }
      if (notes.trim()) {
        reviewParams.notes = notes.trim();
      }
      return scholarshipApi.reviewApplication(reviewParams);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scholarshipApplications', scholarshipId] });
      // Show the discount outcome for approvals; rejections just close.
      if (decision === 'approve' && data.discount) {
        setOutcome(data.discount);
      } else {
        handleClose();
      }
    },
  });

  const handleClose = () => {
    setDecision(null);
    setCoveragePercent(scholarshipCoveragePercent);
    setNotes('');
    setOutcome(null);
    reset();
    onClose();
  };

  const outcomeAlert = (() => {
    if (!outcome) return null;
    if (outcome.applied) {
      return {
        severity: 'success' as const,
        text: t('scholarshipsPage.review.discountApplied', {
          amount: formatNumber(outcome.tuitionDiscountAmount),
          total: formatNumber(outcome.newTotalAmount),
        }),
      };
    }
    if (outcome.reason === 'no_plan') {
      return { severity: 'info' as const, text: t('scholarshipsPage.review.discountNoPlan') };
    }
    if (outcome.reason === 'conflict') {
      return { severity: 'warning' as const, text: t('scholarshipsPage.review.discountConflict') };
    }
    return { severity: 'warning' as const, text: t('scholarshipsPage.review.discountInactive') };
  })();

  if (!application) return null;

  const statusInfo = STATUS_LABELS[application.status] ?? {
    label: application.status,
    color: 'default' as const,
  };
  const isReviewable = application.status === 'submitted' || application.status === 'reviewing';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={700}>
            Revisar Postulación
          </Typography>
          <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error">{errorText(error, 'Error al procesar la revisión')}</Alert>
          )}

          {outcomeAlert && <Alert severity={outcomeAlert.severity}>{outcomeAlert.text}</Alert>}

          {/* Student Info */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1.5} fontWeight={600}>
              INFORMACIÓN DEL POSTULANTE
            </Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonRounded fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  ID:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {application.studentId.slice(0, 12)}…
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <WorkRounded fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Situación laboral:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {EMPLOYMENT_LABELS[application.employmentStatus] ?? application.employmentStatus}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <FamilyRestroomRounded fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Dependientes:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {application.economicDependents}
                </Typography>
              </Stack>
              {application.hadScholarshipBefore && (
                <Chip
                  label={`Beca anterior: ${application.previousScholarshipYear ?? 'Sí'}`}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Financial Statement */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
              SITUACIÓN FINANCIERA
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', bgcolor: 'action.hover', p: 2, borderRadius: 1 }}
            >
              {application.financialStatement}
            </Typography>
          </Box>

          {/* Motivation */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
              CARTA DE MOTIVACIÓN
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', bgcolor: 'action.hover', p: 2, borderRadius: 1 }}
            >
              {application.motivationStatement}
            </Typography>
          </Box>

          {/* Review section — only if reviewable */}
          {isReviewable && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" mb={2} fontWeight={600}>
                  DECISIÓN DEL COMITÉ
                </Typography>
                <ToggleButtonGroup
                  value={decision}
                  exclusive
                  onChange={(_, v) => {
                    if (v) setDecision(v);
                  }}
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="approve" color="success" sx={{ px: 3 }}>
                    <CheckCircleOutlineRounded sx={{ mr: 1 }} fontSize="small" />
                    Aprobar
                  </ToggleButton>
                  <ToggleButton value="reject" color="error" sx={{ px: 3 }}>
                    <CancelOutlined sx={{ mr: 1 }} fontSize="small" />
                    Rechazar
                  </ToggleButton>
                </ToggleButtonGroup>

                {decision === 'approve' && (
                  <Stack spacing={1} mb={2}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Porcentaje aprobado: <strong>{coveragePercent}%</strong>
                    </Typography>
                    <Slider
                      value={coveragePercent}
                      onChange={(_, v) => setCoveragePercent(v as number)}
                      min={10}
                      max={100}
                      step={5}
                      marks={[
                        { value: 25, label: '25%' },
                        { value: 50, label: '50%' },
                        { value: 75, label: '75%' },
                        { value: 100, label: '100%' },
                      ]}
                      valueLabelDisplay="auto"
                      color="success"
                    />
                  </Stack>
                )}

                <TextField
                  label="Notas del comité (opcional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Ej: Se aprueba porque cumple con los requisitos socioeconómicos..."
                />
              </Box>
            </>
          )}

          {/* Already reviewed info */}
          {!isReviewable && application.reviewNotes && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1} fontWeight={600}>
                NOTAS DE REVISIÓN
              </Typography>
              <Typography variant="body2" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                {application.reviewNotes}
              </Typography>
              {application.approvedCoveragePercent != null && (
                <Chip
                  label={`Cobertura aprobada: ${application.approvedCoveragePercent}%`}
                  color="success"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isPending}>
          {outcome ? t('scholarshipsPage.review.close') : isReviewable ? 'Cancelar' : 'Cerrar'}
        </Button>
        {isReviewable && !outcome && (
          <Button
            variant="contained"
            onClick={() => mutate()}
            disabled={isPending || !decision}
            color={decision === 'reject' ? 'error' : 'primary'}
            sx={
              decision !== 'reject'
                ? { background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }
                : {}
            }
          >
            {isPending
              ? 'Guardando...'
              : decision === 'approve'
                ? 'Confirmar Aprobación'
                : decision === 'reject'
                  ? 'Confirmar Rechazo'
                  : 'Confirmar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
